/**
 * Business Products Management Page
 * /businesses/:id/products - 상품/서비스 관리 페이지
 */

import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { buildLoginPath } from "@/lib/auth-navigation";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { unwrapPayload } from "@/lib/queryClient";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const productFormSchema = z.object({
  name: z.string().min(1, "상품명을 입력해주세요").max(200),
  name_ko: z.string().max(200).optional(),
  description: z.string().max(2000).optional(),
  description_ko: z.string().max(2000).optional(),
  price_cents: z.number().int().min(0).optional().or(z.literal("")),
  currency: z.string().default("AUD"),
  category: z.string().max(100).optional(),
  is_available: z.boolean().default(true),
  image_url: z.string().url().optional().or(z.literal("")),
});

type ProductFormData = z.infer<typeof productFormSchema>;

interface Product {
  id: number;
  business_id: string;
  name: string;
  name_ko: string | null;
  description: string | null;
  description_ko: string | null;
  price_cents: number | null;
  currency: string;
  image_url: string | null;
  category: string | null;
  is_available: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface Business {
  id: string;
  name: string;
  categoryId?: number | null;
  subscriptionStatus: string | null;
}

export default function BusinessProductsManagementPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ id: string }>("/businesses/:id/products");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [images, setImages] = useState<string[]>([]);

  const businessId = params?.id;

  // 업체 정보 조회
  const { data: business, isLoading: businessLoading } = useQuery<Business>({
    queryKey: ["/api/businesses", businessId],
    queryFn: async () => {
      if (!businessId) throw new Error("Invalid business ID");
      const response = await fetch(`/api/businesses/${businessId}`);
      if (!response.ok) throw new Error("Failed to fetch business");
      return unwrapPayload<Business>(await response.json());
    },
    enabled: !!businessId,
  });

  // 상품 목록 조회
  const { data: productsData, isLoading: productsLoading } = useQuery<{ items: Product[] }>({
    queryKey: ["/api/businesses", businessId, "products"],
    queryFn: async () => {
      if (!businessId) throw new Error("Invalid business ID");
      const response = await fetch(`/api/businesses/${businessId}/products`);
      if (!response.ok) throw new Error("Failed to fetch products");
      return unwrapPayload<{ items: Product[] }>(await response.json());
    },
    enabled: !!businessId,
  });

  const products = productsData?.items || [];
  const isRestaurant = business?.categoryId === 1;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      currency: "AUD",
      is_available: true,
    },
  });

  // 상품 등록
  const createProductMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      if (!user || !businessId) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const requestBody = {
        ...data,
        price_cents: data.price_cents === "" ? null : data.price_cents,
        image_url: images.length > 0 ? images[0] : data.image_url || null,
      };

      const response = await fetch(`/api/businesses/${businessId}/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "상품 등록에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "products"] });
      toast({
        title: "상품이 등록되었습니다",
      });
      setIsDialogOpen(false);
      reset();
      setImages([]);
      setEditingProduct(null);
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 상품 수정
  const updateProductMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      if (!user || !businessId || !editingProduct) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const requestBody = {
        ...data,
        price_cents: data.price_cents === "" ? null : data.price_cents,
        image_url: images.length > 0 ? images[0] : data.image_url || null,
      };

      const response = await fetch(`/api/businesses/${businessId}/products/${editingProduct.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "상품 수정에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "products"] });
      toast({
        title: "상품이 수정되었습니다",
      });
      setIsDialogOpen(false);
      reset();
      setImages([]);
      setEditingProduct(null);
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 상품 삭제
  const deleteProductMutation = useMutation({
    mutationFn: async (productId: number) => {
      if (!user || !businessId) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const response = await fetch(`/api/businesses/${businessId}/products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "상품 삭제에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "products"] });
      toast({
        title: "상품이 삭제되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ProductFormData) => {
    if (editingProduct) {
      updateProductMutation.mutate(data);
    } else {
      createProductMutation.mutate(data);
    }
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    reset();
    setImages([]);
    setIsDialogOpen(true);
  };

  const openEditDialog = (product: Product) => {
    setEditingProduct(product);
    reset({
      name: product.name,
      name_ko: product.name_ko || "",
      description: product.description || "",
      description_ko: product.description_ko || "",
      price_cents: product.price_cents || undefined,
      currency: product.currency || "AUD",
      category: product.category || "",
      is_available: product.is_available,
      image_url: product.image_url || "",
    });
    setImages(product.image_url ? [product.image_url] : []);
    setIsDialogOpen(true);
  };

  if (!match || !params || !businessId) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 업체 ID입니다</p>
            <Button className="mt-4" onClick={() => navigate("/business-directory")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (businessLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </PageContainer>
    );
  }

  if (!business) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">업체를 찾을 수 없습니다</p>
            <Button className="mt-4" onClick={() => navigate("/business-directory")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">로그인이 필요합니다</p>
            <Button className="mt-4" onClick={() => navigate(buildLoginPath())}>
              로그인하기
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const maxProducts = 10;

  return (
    <>
      <Seo
        title={`${business.name} - ${isRestaurant ? "메뉴" : "상품"} 관리`}
        description={isRestaurant ? "메뉴와 메뉴 이미지를 관리하세요" : "상품/서비스를 관리하세요"}
        path={`/businesses/${businessId}/products`}
      />
      <PageContainer size="read">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/business-directory/${businessId}`)}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{isRestaurant ? "메뉴 관리" : "상품 관리"}</h1>
                <p className="text-muted-foreground">{business.name}</p>
              </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog} disabled={products.length >= maxProducts}>
                    <Plus className="w-4 h-4 mr-2" />
                    {isRestaurant ? "메뉴 추가" : "상품 추가"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingProduct
                        ? isRestaurant ? "메뉴 수정" : "상품 수정"
                        : isRestaurant ? "메뉴 등록" : "상품 등록"}
                    </DialogTitle>
                    <DialogDescription>
                      {editingProduct
                        ? "상품 정보를 수정하세요"
                        : `최대 ${maxProducts}개까지 등록 가능합니다 (현재 ${products.length}개)`}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* 상품명 */}
                    <div className="space-y-2">
                      <Label htmlFor="name">{isRestaurant ? "메뉴명" : "상품명"} (영문) *</Label>
                      <Input id="name" {...register("name")} placeholder="예: Bibimbap" />
                      {errors.name && (
                        <p className="text-sm text-destructive">{errors.name.message}</p>
                      )}
                    </div>

                    {/* 상품명 (한글) */}
                    <div className="space-y-2">
                      <Label htmlFor="name_ko">{isRestaurant ? "메뉴명" : "상품명"} (한글)</Label>
                      <Input id="name_ko" {...register("name_ko")} placeholder="예: 비빔밥" />
                    </div>

                    {/* 설명 */}
                    <div className="space-y-2">
                      <Label htmlFor="description">설명 (영문)</Label>
                      <Textarea
                        id="description"
                        {...register("description")}
                        placeholder="상품 설명을 입력해주세요"
                        rows={3}
                      />
                    </div>

                    {/* 설명 (한글) */}
                    <div className="space-y-2">
                      <Label htmlFor="description_ko">설명 (한글)</Label>
                      <Textarea
                        id="description_ko"
                        {...register("description_ko")}
                        placeholder="상품 설명을 입력해주세요"
                        rows={3}
                      />
                    </div>

                    {/* 가격 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price_cents">가격 (센트)</Label>
                        <Input
                          id="price_cents"
                          type="number"
                          min="0"
                          {...register("price_cents", {
                            setValueAs: (v) => (v === "" ? "" : parseInt(v, 10)),
                          })}
                          placeholder="예: 1500"
                        />
                        <p className="text-sm text-muted-foreground">
                          예: 1500 = $15.00 AUD
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="currency">통화</Label>
                        <Select
                          value={watch("currency") || "AUD"}
                          onValueChange={(value) => setValue("currency", value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AUD">AUD</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="KRW">KRW</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* 카테고리 */}
                    <div className="space-y-2">
                      <Label htmlFor="category">카테고리</Label>
                      <Input
                        id="category"
                        {...register("category")}
                        placeholder="예: food, service, product"
                      />
                    </div>

                    {/* 이미지 */}
                    <div className="space-y-2">
                      <Label>{isRestaurant ? "메뉴 이미지" : "상품 이미지"}</Label>
                      <MarketImageUploader
                        initialImages={images}
                        onImagesChange={setImages}
                        maxImages={1}
                      />
                    </div>

                    {/* 사용 가능 여부 */}
                    <div className="flex items-center justify-between space-x-2">
                      <div className="space-y-0.5">
                        <Label htmlFor="is_available">판매 중</Label>
                        <p className="text-sm text-muted-foreground">
                          체크 해제하면 상품이 비활성화됩니다
                        </p>
                      </div>
                      <Switch
                        id="is_available"
                        checked={watch("is_available")}
                        onCheckedChange={(checked) => setValue("is_available", checked)}
                      />
                    </div>

                    {/* 버튼 */}
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                      >
                        취소
                      </Button>
                      <Button type="submit" disabled={createProductMutation.isPending || updateProductMutation.isPending}>
                        <Save className="w-4 h-4 mr-2" />
                        {(createProductMutation.isPending || updateProductMutation.isPending) ? "저장 중..." : "저장"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
            </Dialog>
          </div>

          {/* Products List */}
          <Card>
            <CardHeader>
              <CardTitle>{isRestaurant ? "메뉴 목록" : "상품 목록"}</CardTitle>
              <CardDescription>
                {products.length}개 / 최대 {maxProducts}개
              </CardDescription>
            </CardHeader>
            <CardContent>
              {productsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">로딩 중...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">
                    등록된 {isRestaurant ? "메뉴" : "상품"}이 없습니다
                  </p>
                  <Button onClick={openCreateDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    첫 {isRestaurant ? "메뉴" : "상품"} 등록하기
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {products.map((product) => (
                    <Card key={product.id}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          {product.image_url && (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-lg">
                                  {product.name_ko || product.name}
                                </h3>
                                {product.name_ko && (
                                  <p className="text-sm text-muted-foreground">{product.name}</p>
                                )}
                                {product.price_cents && (
                                  <p className="text-lg font-bold mt-1">
                                    ${((product.price_cents || 0) / 100).toFixed(2)}{" "}
                                    {product.currency}
                                  </p>
                                )}
                                {product.description_ko || product.description ? (
                                  <p className="text-sm text-muted-foreground mt-2">
                                    {product.description_ko || product.description}
                                  </p>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2">
                                {product.is_available ? (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    판매 중
                                  </span>
                                ) : (
                                  <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                    판매 중지
                                  </span>
                                )}
                                <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEditDialog(product)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="sm">
                                          <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>상품을 삭제하시겠습니까?</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            이 작업은 되돌릴 수 없습니다.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>취소</AlertDialogCancel>
                                          <AlertDialogAction
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            onClick={() => {
                                              deleteProductMutation.mutate(product.id);
                                            }}
                                            disabled={deleteProductMutation.isPending}
                                          >
                                            {deleteProductMutation.isPending ? "삭제 중..." : "삭제"}
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                </>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </>
  );
}
